import { Edge, Node } from 'modules/workflows/types';

type EdgeMap = Record<string, Partial<Edge>[]>;
type NodeMap = Record<string, Node>;
type EdgeMaps = {
  sourcesToTargetsMap: EdgeMap;
  targetsToSourcesMap: EdgeMap;
};

/**
 * Creates a map of edges to look up a source or target
 * @returns an object with sourcesToTargetsMap and targetsToSourcesMap
 */
export const createEdgesMap = (edges: Edge[] = []) => {
  const sourcesToTargetsMap: EdgeMap = {};
  const targetsToSourcesMap: EdgeMap = {};

  edges.forEach((edge) => {
    const { source, target } = edge;
    if (source && target) {
      sourcesToTargetsMap[source] = sourcesToTargetsMap[source] || [];
      sourcesToTargetsMap[source].push(edge);

      targetsToSourcesMap[target] = targetsToSourcesMap[target] || [];
      targetsToSourcesMap[target].push(edge);
    }
  });
  return { sourcesToTargetsMap, targetsToSourcesMap };
};

/**
 * Finds which tiles in a selection of tiles we can add edges in/out from
 * by checking if the tile has a source/target
 */
export const getTilesetBoundaries = (
  ids: string[] = [],
  { sourcesToTargetsMap, targetsToSourcesMap }: EdgeMaps,
) => {
  const inputIds: string[] = [];
  const outputIds: string[] = [];

  ids.forEach((id) => {
    if (!targetsToSourcesMap[id]) inputIds.push(id);
    if (!sourcesToTargetsMap[id]) outputIds.push(id);
  });
  return { inputIds, outputIds };
};

/**
 * Check if the current path is connected to the previously verified paths
 * @returns boolean
 */
export const checkIfPathsConnect = (currentPath: string[], connectedTiles: string[]) =>
  connectedTiles.some((id) => currentPath.includes(id));

/**
 * Find all the selected and connected tiles from a starting tile
 * @returns an array of selected tile ids
 */
export const findSelectedPath = (startId: string, edgesMap: EdgeMap, nodeMap: NodeMap) => {
  const buildPath = (targets: Partial<Edge>[] = [], path: string[] = []) => {
    // loop through all targets and build a path
    targets?.forEach(({ target }) => {
      if (target && nodeMap[target].isSelected && !path.includes(target)) {
        path.push(target);
        buildPath(edgesMap[target], path);
      }
    });
    return path;
  };

  return buildPath(edgesMap[startId], [startId]);
};

/**
 * Since the backend returns a connected tileset, we first need to verify that all selected tiles are connected. This util:
 * 1. Loops through selected tiles and builds a path from a starting tile
 * 2. Check if each path is connected (e.g. selection starts from two different tiles but connect at the bottom)
 */
export const getConnectedTiles = ({
  selectedIds,
  nodeMap,
  edgeMaps: { targetsToSourcesMap, sourcesToTargetsMap },
}: {
  selectedIds: string[];
  nodeMap: NodeMap;
  edgeMaps: EdgeMaps;
}) => {
  let connectedTiles: string[] = [];

  for (let i = 0; i < selectedIds.length; i++) {
    const id = selectedIds[i];
    const sources = targetsToSourcesMap[id] || [];

    // Check if at least one source tile is selected, if not, continue on
    const isSourceSelected = sources.some(({ source }) => source && !!nodeMap[source].isSelected);
    if (!isSourceSelected) {
      const currentPath = findSelectedPath(id, sourcesToTargetsMap, nodeMap);

      // If there are multiple paths, check if they connect, if not, no need to continue
      if (connectedTiles.length && !checkIfPathsConnect(currentPath, connectedTiles)) {
        connectedTiles = [];
        break;
      }
      connectedTiles = [...connectedTiles, ...currentPath];
    }
  }

  return connectedTiles.length > 0;
};

/**
 * Finds the edges outside of the selection of tiles
 */
export const getNeighborEdges = (
  selectedIds: string[] = [],
  nodeMap: NodeMap,
  { sourcesToTargetsMap, targetsToSourcesMap }: EdgeMaps,
) => {
  const sourceEdges: Partial<Edge>[] = [];
  const targetEdges: Partial<Edge>[] = [];

  selectedIds.forEach((id) => {
    const unselectedSources = (targetsToSourcesMap[id] || [])?.filter(
      (edge) => !nodeMap[edge.source || '']?.isSelected,
    );
    const unselectedTargets = (sourcesToTargetsMap[id] || [])?.filter(
      (edge) => !nodeMap[edge.target || '']?.isSelected,
    );
    sourceEdges.push(...unselectedSources);
    targetEdges.push(...unselectedTargets);
  });
  return { sourceEdges, targetEdges };
};

/**
 * Maps edges from existing tiles to new tileset
 */
export const connectEdgesToTileset = ({
  inputIds,
  outputIds,
  initialAddToPath,
  initialSourceEdges,
  initialTargetEdges,
}: {
  inputIds: string[];
  outputIds: string[];
  initialAddToPath?: string;
  initialSourceEdges?: Partial<Edge>[];
  initialTargetEdges?: Partial<Edge>[];
}) => {
  let remappedSourceEdges = [] as Edge[];
  let remappedTargetEdges = [] as Edge[];
  // Add an edge from source tile to tileset input
  if (initialAddToPath) {
    remappedSourceEdges = [{ source: initialAddToPath, target: inputIds[0], sourceAnchor: 1 }];
  }
  // Map every source edge to tileset input
  if (initialSourceEdges) {
    remappedSourceEdges = initialSourceEdges.map((edge) => ({
      ...edge,
      target: inputIds[0],
    }));
  }
  // Map every tileset ouput to target edge
  if (initialTargetEdges) {
    remappedTargetEdges = outputIds.reduce(
      (acc: Partial<Edge>[], id, idx) =>
        initialTargetEdges[idx]
          ? [
              ...acc,
              {
                ...initialTargetEdges[idx],
                source: id,
              },
            ]
          : acc,
      [],
    );
  }

  return { remappedSourceEdges, remappedTargetEdges };
};
